/**
 * THE GLOOM (doc 06, depth 2) — where the ocean starts using light against you.
 *
 * Three of the six species here are built around a lie: the Lanternjaw's bulb,
 * the Pale Lure's arc, and the Broodmother's cargo. So the light is placed
 * deliberately in each — out front on a stalk, high on an arm, or inside the
 * body — rather than parked on an eye, and the read at 4 px per tile is *where*
 * the glow is, not how much of it there is.
 */

import type { CreatureDraw } from '../creature.ts';
import { bodyLean, tailThrow } from '../creature.ts';
import { blob, dome, fan, step, swell, teeth } from '../organic.ts';

/**
 * LANTERNJAW — the light is a lie, the teeth are not.
 *
 * The lure arm out front is the whole silhouette: it is the only species whose
 * outline reaches ABOVE and AHEAD of its own body, which is what separates it
 * from the Gnasher it would otherwise resemble. Cloaked, so most of the time
 * the player sees this shape at 40% through a shimmer.
 */
export const drawLanternjaw: CreatureDraw = (s, c, ctx, p) => {
  const t = tailThrow(ctx);
  const bite = ctx.state === 'attack';
  const lean = bodyLean(ctx);
  blob(s, c.ax - 1 + lean, c.ay, 7, 5, p.ramp.back, p.ramp.belly);
  // the underbite, hinged wider when it commits
  const gape = bite ? 3 : 1;
  s.fillRect(c.ax + 5, c.ay + gape - 2, 5, 3, p.ramp.belly, 'set');
  if (bite) s.fillRect(c.ax + 5, c.ay - 3, 5, 2, p.ramp.maw, 'set');
  teeth(s, c.ax + 6, c.ay + gape - 3, 3, 2, p.ramp.bone);
  teeth(s, c.ax + 5, c.ay + gape + 1, 3, 2, p.ramp.bone);
  // the stalk, arcing forward over the head, with the bulb at the tip
  const lift = t > 0 ? 1 : 0;
  s.vline(c.ax + 1, c.ay - 8, c.ay - 5, p.ramp.fin, 'set');
  s.px(c.ax + 2, c.ay - 9, p.ramp.fin);
  s.px(c.ax + 4, c.ay - 9 + lift, p.ramp.fin);
  const bulb = c.ay - 9 + lift;
  s.px(c.ax + 5, bulb, p.glow);
  s.px(c.ax + 6, bulb, p.halo);
  s.px(c.ax + 5, bulb - 1, p.halo);
  s.vline(c.ax - 9 + lean, c.ay - 3 + t, c.ay + 3 + t, p.ramp.fin, 'set');
};

/**
 * HAGFISH SPITTER — a tube with a grudge.
 *
 * The only fish down here with no fins at all, and the only body that is one
 * continuous undulating line: doc 03 stops it four tiles out to spit, so the
 * silhouette has to be legible at range with nothing beating on it.
 */
export const drawHagfish: CreatureDraw = (s, c, ctx, p) => {
  const WAVE = [0, -1, -1, 0, 1, 1];
  const shift = step(ctx.phase, WAVE.length) * (ctx.state === 'idle' ? 1 : 2);
  for (let k = -12; k <= 5; k++) {
    const off = WAVE[(((k + shift) % 6) + 6) % 6]!;
    const half = k > 1 ? 2 : k > -8 ? 1 : 0;
    s.vline(c.ax + k, c.ay - half + off, c.ay + half + off, k % 2 ? p.ramp.back : p.ramp.belly, 'set');
  }
  // The snout FLARES: a trumpet, wider at the opening than the tube behind it,
  // because a tube with a lump on the end is a squiggle and not an animal.
  for (let k = 0; k < 5; k++) {
    const h = 2 + k;
    s.vline(c.ax + 6 + k, c.ay - h, c.ay + h, k < 3 ? p.ramp.back : p.ramp.belly, 'set');
  }
  s.fillRect(c.ax + 9, c.ay - 3, 2, 7, p.ramp.maw, 'set'); // the barrel of it
  if (ctx.state === 'attack') {
    // the spit: a jet leaving the snout, which is the only warning you get
    s.px(c.ax + 12, c.ay, p.glow);
    s.px(c.ax + 14, c.ay, p.halo);
    s.px(c.ax + 16, c.ay - 1, p.halo);
  }
  s.px(c.ax + 10, c.ay, p.glow);
  s.px(c.ax + 5, c.ay - 3, p.ramp.bone); // the one dead eye
  s.fillRect(c.ax, c.ay - 2, 3, 3, p.halo, 'set'); // the sac, always visibly full
};

/**
 * IRONSCALE RAY — one plate, swept.
 *
 * Fast AND thick, which doc 06 admits is deeply unfair. Drawn as a single solid
 * delta so it never reads as the Reef Tyrant, which is two wings with a gap
 * between them. The armour bands and the speed streaks both arrive as markers.
 */
export const drawIronscale: CreatureDraw = (s, c, ctx, p) => {
  const beat = ctx.state === 'attack' ? 2 : tailThrow(ctx);
  for (let k = 0; k <= 18; k++) {
    const x = c.ax + 12 - k;
    const h = Math.min(7, Math.round(k * 0.42));
    const wob = k > 13 ? beat : 0;
    s.vline(x, c.ay - h + wob, c.ay + h + wob, k % 2 ? p.ramp.back : p.ramp.belly, 'set');
  }
  s.hline(c.ax - 12, c.ax - 7, c.ay + beat, p.ramp.fin, 'set'); // the stinger
  if (ctx.state === 'attack') {
    s.px(c.ax - 13, c.ay + beat, p.ramp.bone);
    s.px(c.ax - 13, c.ay - 1 + beat, p.ramp.bone);
  }
  s.px(c.ax + 11, c.ay - 1, p.glow);
  s.px(c.ax + 11, c.ay + 1, p.glow);
};

/**
 * BROODMOTHER JELLY — a piñata. Do not celebrate.
 *
 * The only round-topped, downward-trailing outline in the game: everything else
 * tapers to a tail behind it, this one hangs. The brood glowing THROUGH the
 * bell is the joke doc 06 wrote, and the reason it is worth twelve krill.
 */
export const drawBroodmother: CreatureDraw = (s, c, ctx, p) => {
  // a jelly swims by contracting: the bell pulses twice as hard on the move
  const pulse = ctx.state === 'attack' ? 3 : swell(ctx.phase) * (ctx.state === 'move' ? 2 : 1);
  dome(s, c.ax, c.ay - 1, 8 + pulse, 6, p.ramp.back, p.ramp.belly, 0.62);
  s.hline(c.ax - 8 - pulse, c.ax + 7 + pulse, c.ay + 2, p.ramp.belly, 'set');
  for (const [dx, dy] of [
    [-4, -2],
    [0, -4],
    [4, -2],
    [-2, 0],
    [3, 0],
  ] as const) {
    s.px(c.ax + dx, c.ay + dy, p.halo);
  }
  // tendrils, trailing and swaying — longer and stiffer when it is stinging
  const reach = ctx.state === 'attack' ? 2 : 0;
  for (const dx of [-6, -3, 0, 3, 6]) {
    const sway = ctx.state === 'attack' ? 0 : [0, 1, 0, -1][step(ctx.phase + Math.abs(dx) / 4, 4)]!;
    const len = c.ay + 8 + reach + (dx % 2 === 0 ? 2 : 0);
    s.vline(c.ax + dx, c.ay + 3, len, p.ramp.fin, 'set');
    s.px(c.ax + dx + sway, len + 2, p.halo);
  }
};

/**
 * THE PALE LURE (mini-boss) — a Lanternjaw that got funded.
 *
 * Doc 06 gives it a cloak metronome and an escort it keeps calling, so both are
 * in the sprite: the long arched arm ending in the light everything follows,
 * and four dim lights already orbiting it. Pale flesh, which is `bone` over the
 * body rather than a second base — Law L5 has no room for a private palette.
 */
export const drawPaleLure: CreatureDraw = (s, c, ctx, p) => {
  const t = tailThrow(ctx);
  const bite = ctx.state === 'attack';
  blob(s, c.ax - 2, c.ay, 12, 8, p.ramp.bone, p.ramp.back);
  const gape = bite ? 6 : 4;
  s.fillRect(c.ax + 8, c.ay - gape, 8, 4, p.ramp.bone, 'set');
  s.fillRect(c.ax + 8, c.ay + gape - 2, 8, 4, p.ramp.back, 'set');
  if (bite) s.fillRect(c.ax + 9, c.ay - 2, 7, 3, p.ramp.maw, 'set');
  teeth(s, c.ax + 9, c.ay - gape + 3, 4, 2, p.ramp.belly);
  teeth(s, c.ax + 9, c.ay + gape - 3, 4, 2, p.ramp.belly);
  // the arm: a long arc ending in the light the whole shoal is following
  const lift = t > 0 ? 1 : 0;
  for (let k = 0; k < 7; k++) {
    s.px(c.ax + 1 + k, c.ay - 12 - (k < 3 ? k : 6 - k) + lift, p.ramp.fin);
  }
  const bx = c.ax + 8;
  const by = c.ay - 13 + lift;
  s.fillRect(bx, by, 2, 2, p.glow, 'set');
  for (const [dx, dy] of [
    [-1, 0],
    [2, 0],
    [0, -1],
    [0, 2],
  ] as const) {
    s.px(bx + dx, by + dy, p.halo);
  }
  // the escort it has already called
  for (const [dx, dy] of [
    [-12, -7],
    [-14, 3],
    [-8, 8],
    [2, -10],
  ] as const) {
    s.px(c.ax + dx, c.ay + dy + t, p.halo);
  }
  fan(s, c.ax - 11, c.ay, 5, 5, t, p.ramp.fin);
};

/**
 * LEVIATHAN CALF (boss) — bulk with a mouth at the front.
 *
 * The widest thing in the water. Doc 06 walks it straight at the Core through
 * whatever you built, so the silhouette is a slab: no wings, no legs, no arm,
 * nothing but a gape and a fluke. The bite pose closes the jaw on the wall.
 */
export const drawLeviathan: CreatureDraw = (s, c, ctx, p) => {
  const beat = tailThrow(ctx);
  const bite = ctx.state === 'attack';
  const lean = bodyLean(ctx);
  // Body BACK, gape FORWARD. The first draft overlapped the two and the mouth
  // vanished into the mass: what shipped read as a capsule with a pipe on it.
  blob(s, c.ax - 6 + lean, c.ay, 14, 8, p.ramp.back, p.ramp.belly);
  const gape = bite ? 2 : 8;
  s.fillRect(c.ax + 4, c.ay - gape - 1, 17, 5, p.ramp.back, 'set');
  s.fillRect(c.ax + 4, c.ay + gape - 4, 17, 5, p.ramp.belly, 'set');
  s.fillRect(c.ax + 5, c.ay - gape + 3, 16, gape * 2 - 6, p.ramp.maw, 'set');
  teeth(s, c.ax + 6, c.ay - gape + 3, 5, 3, p.ramp.bone);
  teeth(s, c.ax + 8, c.ay + gape - 4, 4, 3, p.ramp.bone);
  // gill slits and a dorsal ridge — the bulk needs texture or it reads flat
  for (let k = 0; k < 7; k++) s.px(c.ax - 10 + k * 2, c.ay - 9 + (k & 1), p.ramp.bone);
  for (let k = 0; k < 5; k++) s.hline(c.ax - 4 - k, c.ax + 1 - k, c.ay + 7 + k, p.ramp.belly, 'set');
  fan(s, c.ax - 16, c.ay, 7, 7, beat, p.ramp.fin);
  s.px(c.ax + 9, c.ay - 5, p.glow); // one small, uninterested eye
  s.px(c.ax + 10, c.ay - 5, p.halo);
};
